import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { useTenantProfile } from '../../lib/settings';
import { ClauseEditor, type Clause } from '../sales/ClauseEditor';

/**
 * العقود: التوقيع والبنود الافتراضية — بطاقةٌ قائمةٌ بنفسها.
 *
 * كانت في «العمولات والخصومات» تشارك العمولاتِ حفظًا واحدًا، وموضعُها من
 * النظام حيث يُصنع المستند: الختمُ والبنودُ تُطبع على العقد كما يُطبع الشعار
 * والتذييل على الفاتورة، فجمعُها مع «الهوية والمستندات» أقرب.
 *
 * وحفظُها جزئيٌّ على مسار التفضيلات (`/settings/preferences`) بحقولها الأربعة
 * وحدها: الخادم يدمج المُرسَل فوق المحفوظ، فلا يمسّ عمولةً ولا ضريبة.
 */
export function ContractSettingsCard() {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');

  const [signerName, setSignerName] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [autoSign, setAutoSign] = useState(false);
  const [clauses, setClauses] = useState<Clause[]>([]);
  const [dirty, setDirty] = useState(false);

  /* تُملأ من الخادم مرة واحدة: المزامنة الدائمة تمحو ما يكتبه المستخدم. */
  useEffect(() => {
    const s = profile.data?.settings;
    if (!s || dirty) return;
    setSignerName(s.contractSignerName ?? '');
    setSignatureUrl(s.contractSignatureUrl ?? '');
    setAutoSign(Boolean(s.contractAutoSign));
    setClauses(
      (s.defaultContractClauses ?? []).map((c, i) => ({
        id: `d${i}`,
        title: c.title,
        body: c.body,
      })),
    );
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/preferences', {
        method: 'PATCH',
        body: {
          contractSignerName: signerName.trim(),
          contractSignatureUrl: signatureUrl,
          contractAutoSign: autoSign,
          /* المعرّف محلّيّ للتحرير وحده؛ المحفوظ عنوانٌ ونصّ. */
          defaultContractClauses: clauses
            .filter((c) => c.body.trim())
            .map((c) => ({ title: c.title, body: c.body })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDirty(false);
    },
  });

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">العقود: التوقيع والبنود الافتراضية</h2>
        {canEdit && (
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        )}
      </div>

      <div className="card-body">
        {!canEdit && (
          <div className="alert alert-info mb-4">
            للعرض فقط — تعديل التوقيع وبنود العقود من صلاحية المدير.
          </div>
        )}
        {save.isError && (
          <div className="alert alert-danger mb-4">
            {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
          </div>
        )}
        {save.isSuccess && !dirty && (
          <div className="alert alert-success mb-4">حُفظت إعدادات العقود.</div>
        )}

        {/*
          الحدّ يُقال قبل الإعداد لا بعده.
          «توقيع إلكتروني» تُفهم توثيقًا للهوية، وهذا ختمُ شركةٍ يوفّر خطوة
          الطبع واليد. قولُه هنا يمنع بناء ثقةٍ على ما لا يفعله.
        */}
        <div className="alert alert-info mb-4">
          هذا ختمُ الشركة: صورةُ توقيعٍ تُطبع على العقد باسم موقّعها وتاريخه، فتوفّر خطوة
          الطبع والتوقيع اليدوي. وهو ليس توقيعًا موثَّقًا ولا يُثبت هويّة العميل أو موافقته.
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="sig-name">
              اسم الموقِّع عن الشركة
            </label>
            <input
              id="sig-name"
              className="input"
              value={signerName}
              disabled={!canEdit}
              onChange={(e) => {
                setSignerName(e.target.value);
                setDirty(true);
              }}
              placeholder="محمد عبد الله — المدير التنفيذي"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="sig-file">
              صورة التوقيع
            </label>
            <input
              id="sig-file"
              className="input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={!canEdit}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                /*
                 * الحدّ مئتا كيلوبايت: الصورة تُحفظ نصًّا في الإعدادات
                 * وتُنسخ في كل عقدٍ يُوقَّع، فملفٌ ضخم يثقل كل قراءة.
                 */
                if (file.size > 200_000) {
                  window.alert('حجم الصورة أكبر من ٢٠٠ كيلوبايت. اختر صورةً أصغر.');
                  e.target.value = '';
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                  setSignatureUrl(String(reader.result ?? ''));
                  setDirty(true);
                };
                reader.readAsDataURL(file);
              }}
            />
            <span className="tiny mute">صورةٌ بخلفية شفافة أوضح على المستند.</span>
          </div>

          {signatureUrl && (
            <div className="field span-2">
              <div className="signature-box">
                <img src={signatureUrl} alt="توقيع الشركة" />
                <div className="col" style={{ gap: 4 }}>
                  <span className="strong">{signerName || 'بلا اسم موقِّع'}</span>
                  {canEdit && (
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        setSignatureUrl('');
                        setDirty(true);
                      }}
                    >
                      إزالة التوقيع
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="field span-2">
            <label className="row" style={{ cursor: canEdit ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={autoSign}
                disabled={!canEdit || !signatureUrl}
                onChange={(e) => {
                  setAutoSign(e.target.checked);
                  setDirty(true);
                }}
              />
              <span className="small">توقيعٌ تلقائيّ عند تفعيل العقد</span>
            </label>
            <span className="tiny mute">
              {signatureUrl
                ? 'يُختم العقد لحظة تفعيله بلا خطوةٍ يدوية.'
                : 'أضف صورة التوقيع أولًا ليُمكن تفعيل التوقيع التلقائي.'}
            </span>
          </div>
        </div>

        <div className="mt-4">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>بنود العقد الافتراضية</strong>
            <span className="tiny mute">تُدرَج في كل عقدٍ جديد</span>
          </div>
          <p className="tiny mute mb-4">
            تُنسخ في العقد لحظة إنشائه، فتعديلها هنا لا يمسّ عقدًا قائمًا.
          </p>
          <ClauseEditor
            clauses={clauses}
            onChange={(next) => {
              setClauses(next);
              setDirty(true);
            }}
            disabled={!canEdit}
          />
        </div>
      </div>
    </div>
  );
}
